import { useState, useEffect } from 'react';
import { Box, Button, Grid, InputAdornment } from '@material-ui/core';
import { FrontLayout } from 'components';
import { PasswordIcon, EmailIcon } from 'components/Icons';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { ISignUpRequestPayload } from 'shared/interfaces/IUser';
import { selectUserState, userSignup } from 'shared/redux/slicers/user.slicer';
import * as yup from 'yup';
import { FormikProps, useFormik } from 'formik';
import { getErrorMessage } from 'shared/utils/getErrorMessage';
import { PasswordPrinciple, validatePassword } from 'shared/utils/passwordUtil';
import { Input, InputPassword, Select, Backdrop, PasswordStrength } from '@room-match/shared-ui-components';
import { ContactInput } from 'shared/elements/ContactInput';
import countriesList from 'data/Countries.json';
import stateLessCountries from 'data/StateLessCountries.json';
import statesList from 'data/States.json';
import { useStyles } from './Signup.styles';

const Signup = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [, setPasswordValidationResult] = useState<PasswordPrinciple | null>(null);
  const [states, setStates] = useState<any>([]);
  const [password_str, setPasswordStr] = useState<string>('');

  const { isLoading, isLoggedIn } = useSelector(selectUserState);

  useEffect(() => {
    if (isLoggedIn) {
      // history.push(ROUTES.TALENT.PROFILE);
    }
  }, [isLoggedIn, history]);

  // Forms
  const initialValues: ISignUpRequestPayload = {
    first_name: '',
    last_name: '',
    contact_no: null,
    email: '',
    country: '',
    country_code: '1',
    state_region: '',
    password: '',
    password_confirmation: '',
    user_type: 'casting',
  };

  const countries = countriesList.map((country) => ({ key: country.name, value: country.code }));

  const handleStateLessCountries = (countryCode: any) => {
    return stateLessCountries.includes(countryCode);
  };

  const signUpValidationSchema: yup.SchemaOf<ISignUpRequestPayload> = yup.object({
    first_name: yup.string().required('First name is required'),
    last_name: yup.string().required('Last name is required'),
    contact_no: yup
      .number()
      .typeError('Contact number must be numbers only')
      .positive('Contact number must be greater than zero')
      .required('Contact number is required'),
    email: yup.string().email('Wrong email format').required('Email is required'),
    country: yup.string().required('Country is required'),
    country_code: yup.string().required('Country code is required'),
    state_region: yup.string().when('country', {
      is: (val: any) => handleStateLessCountries(val),
      then: yup.string().notRequired(),
      otherwise: yup.string().required('State is required'),
    }),
    user_type: yup.string().required('User type is required'),
    password: yup
      .string()
      .required('Password is required')
      .min(8, 'Password must be atleast 8 characters')
      .test('passwordValidate', 'Invalid password', (value: any) => {
        if (value) {
          const validatePasswordResult = validatePassword(value);
          setPasswordValidationResult(validatePasswordResult);
          return validatePasswordResult.IsValid;
        }
        return false;
      }),
    password_confirmation: yup
      .string()
      .required('Password confirmation is required')
      .oneOf([yup.ref('password')], 'Your passwords do not match.'),
  });

  const handleSetCountryStates = (countryCode: any) => {
    const filteredStates = statesList.filter((state) => state.countryCode === countryCode);
    const newStates = filteredStates.map((state) => ({ key: state.name, value: state.name }));
    setStates(newStates);
  };

  const handleSignUpSubmit = async (values: ISignUpRequestPayload) => {
    dispatch(userSignup(values));
  };

  const form: FormikProps<ISignUpRequestPayload> = useFormik({
    initialValues,
    validationSchema: signUpValidationSchema,
    onSubmit: (values) => handleSignUpSubmit(values),
  });

  return (
    <FrontLayout heading="Let’s create your new" subHeading="Account Now" containerWidth={800}>
      <Box className={classes.boxContainer}>
        <Grid container spacing={2}>
          <Grid xs={12} sm={6} md={6} lg={6} item>
            <Input
              variant="outlined"
              placeholder="First Name"
              name="first_name"
              autoFocus
              onChange={(e) => {
                if (form.errors.first_name && !form.touched.first_name) {
                  form.setFieldTouched('first_name');
                  form.validateField('first_name');
                }
                return form.handleChange(e);
              }}
              errorMessage={getErrorMessage(form.touched.first_name, form.errors.first_name)}
              value={form.values.first_name}
              fullWidth
              margin={'normal'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon style={{ marginTop: '8px' }} />
                  </InputAdornment>
                ),
              }}
              inputProps={{ tabIndex: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <Input
              variant="outlined"
              placeholder="Last Name"
              name="last_name"
              onChange={(e) => {
                if (form.errors.last_name && !form.touched.last_name) {
                  form.setFieldTouched('last_name');
                  form.validateField('last_name');
                }
                return form.handleChange(e);
              }}
              errorMessage={getErrorMessage(form.touched.last_name, form.errors.last_name)}
              value={form.values.last_name}
              fullWidth
              margin={'normal'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon style={{ marginTop: '8px' }} />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2 }}
            />
            <Box className={classes.customContactInput}>
              <ContactInput
                name="contact_no"
                variant="outlined"
                handleCodeChange={(val: any) => {
                  form.setFieldValue('country_code', val);
                }}
                country={form.values.country.toLowerCase()}
                onChange={(e) => {
                  if (form.errors.contact_no && !form.touched.contact_no) {
                    form.setFieldTouched('contact_no');
                    form.validateField('contact_no');
                  }
                  return form.handleChange(e);
                }}
                errorMessage={getErrorMessage(form.touched.contact_no, form.errors.contact_no)}
                value={form.values.contact_no}
                dropdownStyle={{ height: '60px' }}
                showLabel={false}
                placeholder="Contact Number"
              />
            </Box>
            <Input
              variant="outlined"
              placeholder="Email Address"
              name="email"
              onChange={(e) => {
                if (form.errors.email && !form.touched.email) {
                  form.setFieldTouched('email');
                  form.validateField('email');
                }
                return form.handleChange(e);
              }}
              errorMessage={getErrorMessage(form.touched.email, form.errors.email)}
              value={form.values.email}
              fullWidth
              margin={'normal'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon style={{ marginTop: '8px' }} />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 5 }}
            />
            <Box>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Select
                    variant="outlined"
                    placeHolder="Country"
                    displayEmpty
                    fullWidth
                    data={countries}
                    value={form.values.country}
                    onChange={(event) => {
                      form.setFieldValue('country', event.target.value);
                      form.setFieldValue('state_region', '');
                      handleSetCountryStates(event.target.value);
                    }}
                    errorMessage={getErrorMessage(form.touched.country, form.errors.country)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Select
                    variant="outlined"
                    placeHolder="States"
                    fullWidth
                    data={states}
                    value={form.values.state_region}
                    onChange={(event) => form.setFieldValue('state_region', event.target.value)}
                    errorMessage={getErrorMessage(form.touched.state_region, form.errors.state_region)}
                    disabled={states.length === 0}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid xs={12} sm={6} md={6} lg={6} item>
            <Box>
              <InputPassword
                variant="outlined"
                placeholder="New Password"
                margin={'normal'}
                name="password"
                onChange={(e) => {
                  if (form.errors.password && !form.touched.password) {
                    form.setFieldTouched('password');
                    form.validateField('password');
                  }
                  setPasswordStr(e.target.value || '');
                  return form.handleChange(e);
                }}
                errorMessage={getErrorMessage(form.touched.password, form.errors.password)}
                value={form.values.password}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PasswordIcon style={{ marginTop: '8px' }} />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ tabIndex: 8 }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
              />
              <InputPassword
                variant="outlined"
                placeholder="Confirm Password"
                margin={'normal'}
                name="password_confirmation"
                fullWidth
                onChange={(e) => {
                  if (form.errors.password_confirmation && !form.touched.password_confirmation) {
                    form.setFieldTouched('password_confirmation');
                    form.validateField('password_confirmation');
                  }
                  return form.handleChange(e);
                }}
                errorMessage={getErrorMessage(form.touched.password_confirmation, form.errors.password_confirmation)}
                value={form.values.password_confirmation}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PasswordIcon style={{ marginTop: '8px' }} />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ tabIndex: 8 }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
              />
              <PasswordStrength password={password_str} />
            </Box>
          </Grid>
          <Grid xs={12} md={12} lg={12} className={classes.buttonContainer}>
            <Button
              color="primary"
              variant="contained"
              disableElevation
              fullWidth
              tabIndex={10}
              onClick={() => form.handleSubmit()}
              style={{ height: '60px' }}
            >
              Create Account
            </Button>
            <Button
              variant="outlined"
              disableElevation
              fullWidth
              style={{ marginTop: 10, height: '60px' }}
              component={Link}
              to={'/login'}
              tabIndex={11}
            >
              Cancel to Log In
            </Button>
          </Grid>
          <Grid xs={12} md={12} lg={12}>
            <Backdrop isLoading={isLoading} />
          </Grid>
        </Grid>
      </Box>
    </FrontLayout>
  );
};

export default Signup;
